import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import CreatePitchEventModal from '../components/CreatePitchEventModal';
import CreateStartupModal from '../components/CreateStartupModal';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import FileUpload from '../components/FileUpload';
import FileList from '../components/FileList';
import FilePreviewModal from '../components/FilePreviewModal';
import { adminAPI, pitchEventAPI, fileAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';

const PitchEventDetail = () => {
    const { id, tenantId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [event, setEvent] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddStartupModal, setShowAddStartupModal] = useState(false);
    const [showCreateStartupModal, setShowCreateStartupModal] = useState(false);
    const [showAddInvestorModal, setShowAddInvestorModal] = useState(false);
    const [startups, setStartups] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [availableStartups, setAvailableStartups] = useState([]);
    const [availableInvestors, setAvailableInvestors] = useState([]);
    const [eventInvestors, setEventInvestors] = useState([]);
    const [loadingInvestors, setLoadingInvestors] = useState(false);
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [openDropdown, setOpenDropdown] = useState(null);
    const [files, setFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);


    useEffect(() => {
        loadEventDetails();
        loadParticipants();
        loadStats();
        loadStartups();
        loadInvestors();
        loadFiles();
    }, [id]);

    useEffect(() => {
        setSearchParams({ tab: activeTab });
    }, [activeTab, setSearchParams]);

    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && ['overview', 'pipeline', 'participants', 'statistics', 'startups', 'investors', 'files'].includes(tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    // Update available startups when participants or startups change
    useEffect(() => {
        if (startups.length > 0) {
            updateAvailableStartups(startups);
        }
    }, [participants, startups]);

    const loadEventDetails = async () => {
        setLoading(true);
        try {
            const response = await fetch(getApiUrl(`pitch-events/${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            setEvent(data);
        } catch (error) {
            console.error('Failed to load event details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadParticipants = async () => {
        try {
            const response = await fetch(getApiUrl(`pitch-events/${id}/participants`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to load participants: ${response.status}`);
            }

            const data = await response.json();
            setParticipants(data || []);

            // Update available startups after participants are loaded
            if (startups.length > 0) {
                updateAvailableStartups(startups);
            }
        } catch (error) {
            console.error('Failed to load participants:', error);
            setParticipants([]);
        }
    };

    const loadStats = async () => {
        try {
            const response = await fetch(getApiUrl(`pitch-events/${id}/stats`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            setStats(data);
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const updateAvailableStartups = (allStartups) => {
        // Filter out startups that are already participants
        const participantStartupIds = participants.map(p => p.startup?.id || p.startupId).filter(Boolean);
        setAvailableStartups(allStartups.filter(s => !participantStartupIds.includes(s.id)));
    };

    const loadStartups = async () => {
        try {
            const data = await adminAPI.getStartups();
            setStartups(data || []);
            // updateAvailableStartups will be called via useEffect when participants are loaded
        } catch (error) {
            console.error('Failed to load startups:', error);
            setStartups([]);
        }
    };

    const loadInvestors = async () => {
        setLoadingInvestors(true);
        try {
            // Load all available investors
            const allInvestors = await adminAPI.getInvestors();

            // Load investors already added to the event
            const currentInvestors = await pitchEventAPI.getEventInvestors(id);
            setEventInvestors(currentInvestors || []);

            // Filter out already added investors
            const currentInvestorIds = (currentInvestors || []).map(inv => inv.id);
            const available = (allInvestors || []).filter(inv => !currentInvestorIds.includes(inv.id));

            setInvestors(allInvestors || []);
            setAvailableInvestors(available);
        } catch (error) {
            console.error('Failed to load investors:', error);
            setInvestors([]);
            setAvailableInvestors([]);
            setEventInvestors([]);
        } finally {
            setLoadingInvestors(false);
        }
    };

    const loadFiles = async () => {
        setLoadingFiles(true);
        try {
            const response = await fetch(getApiUrl(`files?moduleType=PITCH_EVENT&moduleId=${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFiles(data || []);
            } else if (response.status === 204) {
                setFiles([]);
            }
        } catch (error) {
            console.error('Failed to load files:', error);
            setFiles([]);
        } finally {
            setLoadingFiles(false);
        }
    };

    // Update available investors when eventInvestors change
    useEffect(() => {
        if (investors.length > 0) {
            const eventInvestorIds = eventInvestors.map(ei => ei.id);
            setAvailableInvestors(investors.filter(inv => !eventInvestorIds.includes(inv.id)));
        }
    }, [eventInvestors, investors]);

    // Load investors when add investor modal opens
    useEffect(() => {
        if (showAddInvestorModal) {
            loadInvestors();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAddInvestorModal]);

    const handleAddStartup = async (startupId) => {
        try {
            const response = await fetch(getApiUrl(`pitch-events/${id}/participants`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ startupId })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
                throw new Error(errorData.message || 'Failed to add startup');
            }

            setShowAddStartupModal(false);
            await loadParticipants();
            updateAvailableStartups(startups);
            setAlert({ isOpen: true, title: 'Success', message: 'Startup added successfully!', type: 'success' });
        } catch (error) {
            console.error('Failed to add startup:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to add startup: ' + (error.message || 'Unknown error'), type: 'error' });
        }
    };

    const handleStartupCreated = async (startupData) => {
        try {
            // The startupData should contain the newly created startup (User object) with its ID
            // The API returns a User object with role STARTUP
            const startupId = startupData?.id;

            if (!startupId) {
                console.error('Startup data received:', startupData);
                throw new Error('Startup ID not found in created startup data');
            }

            // Automatically add the newly created startup to the event
            await handleAddStartup(startupId);

            // Close the create startup modal
            setShowCreateStartupModal(false);

            // Reload startups list to include the new one
            await loadStartups();

            setAlert({
                isOpen: true,
                title: 'Success',
                message: 'Startup created and added to event successfully!',
                type: 'success'
            });
        } catch (error) {
            console.error('Failed to link created startup to event:', error);
            setAlert({
                isOpen: true,
                title: 'Warning',
                message: 'Startup created but failed to add to event: ' + (error.message || 'Unknown error') + '. You can add it manually using "Add Existing Startup".',
                type: 'warning'
            });
            // Still close the modal and reload
            setShowCreateStartupModal(false);
            await loadStartups();
        }
    };

    const handleMoveStage = async (participantId, newStage) => {
        try {
            await pitchEventAPI.updateParticipantStage(id, participantId, newStage);
            await loadParticipants();
            await loadStats();
            setAlert({ isOpen: true, title: 'Success', message: 'Startup moved to ' + getStageLabel(newStage) + ' successfully!', type: 'success' });
        } catch (error) {
            console.error('Failed to move stage:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to move startup: ' + (error.message || 'Unknown error'), type: 'error' });
        }
    };

    const getStageLabel = (stage) => {
        const stageMap = {
            'APPLICATION_RECEIVED': 'Application Received',
            'SCREENING': 'Screening',
            'PRELIMINARY': 'Preliminary',
            'FINAL': 'Final Round',
            'SELECTED': 'Selected'
        };
        return stageMap[stage] || stage;
    };

    const getNextStages = (currentStage) => {
        const stageOrder = ['APPLICATION_RECEIVED', 'SCREENING', 'PRELIMINARY', 'FINAL', 'SELECTED'];
        const currentIndex = stageOrder.indexOf(currentStage);
        if (currentIndex === -1 || currentIndex === stageOrder.length - 1) {
            return [];
        }
        return stageOrder.slice(currentIndex + 1);
    };

    const handleAddInvestor = async (investorId) => {
        try {
            await pitchEventAPI.addEventInvestor(id, investorId);
            await loadInvestors();
            setShowAddInvestorModal(false);
            setAlert({ isOpen: true, title: 'Success', message: 'Investor added successfully!', type: 'success' });
        } catch (error) {
            console.error('Failed to add investor:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to add investor: ' + (error.message || 'Unknown error'), type: 'error' });
        }
    };

    const handleRemoveInvestor = (investorId, participantId, investorName) => {
        setConfirm({
            isOpen: true,
            title: 'Remove Investor',
            message: `Are you sure you want to remove ${investorName} from this event?`,
            type: 'warning',
            onConfirm: async () => {
                await performRemoveInvestor(investorId, participantId);
            }
        });
    };

    const performRemoveInvestor = async (investorId, participantId) => {
        try {
            // For investors, we use the investorId directly since that's how we linked them
            await pitchEventAPI.removeEventInvestor(id, investorId);

            // Reload the list
            await loadInvestors();
            setAlert({ isOpen: true, title: 'Success', message: 'Investor removed successfully!', type: 'success' });
        } catch (error) {
            console.error('Failed to remove investor:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to remove investor: ' + (error.message || 'Unknown error'), type: 'error' });
        }
    };

    const handleDelete = async () => {
        try {
            await pitchEventAPI.deleteEvent(id);
            setAlert({ isOpen: true, title: 'Success', message: 'Event deleted successfully!', type: 'success' });
            setTimeout(() => {
                // Navigate based on user role
                if (user?.role === 'EVENT_ORGANIZER') {
                    navigate(`/${tenantId}/organizer/dashboard`);
                } else {
                    navigate(`/${tenantId}/events`);
                }
            }, 1000);
        } catch (error) {
            console.error('Failed to delete event:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete event: ' + (error.message || 'Unknown error'), type: 'error' });
        }
    };

    const handleBack = () => {
        // Navigate based on user role
        if (user?.role === 'EVENT_ORGANIZER') {
            navigate(`/${tenantId}/organizer/dashboard`);
        } else {
            navigate(`/${tenantId}/events`);
        }
    };

    const getParticipantsByStage = (stage) => {
        return participants.filter(p => (p.stage || p.currentStage) === stage);
    };

    const stages = [
        { id: 'APPLICATION_RECEIVED', label: 'Applications', color: 'blue', icon: '📝' },
        { id: 'SCREENING', label: 'Screening', color: 'yellow', icon: '🔍' },
        { id: 'PRELIMINARY', label: 'Preliminary', color: 'purple', icon: '🎤' },
        { id: 'FINAL', label: 'Final Round', color: 'orange', icon: '🏆' },
        { id: 'SELECTED', label: 'Selected', color: 'green', icon: '✅' }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900">Event not found</h2>
                    <button onClick={handleBack} className="mt-4 text-blue-600 hover:text-blue-800">
                        ← Back {user?.role === 'EVENT_ORGANIZER' ? 'to Dashboard' : 'to Events'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Page Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button onClick={handleBack} className="text-gray-600 hover:text-gray-900">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">{event.eventName}</h1>
                                <div className="flex items-center space-x-3 mt-1">
                                    <span className="text-sm text-gray-600 flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>{new Date(event.eventDate).toLocaleDateString()}</span>
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-sm text-gray-600 flex items-center space-x-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span>{event.location}</span>
                                    </span>
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {event.eventType}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowEditModal(true)}
                                className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition"
                                title="Edit Event"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                            </button>
                            {/* Only show delete button for ADMIN and TENANT_ADMIN roles */}
                            {(user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN') && (
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition"
                                    title="Delete Event"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="mt-4 border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {[
                                { id: 'overview', label: 'Overview' },
                                { id: 'startups', label: 'Startups' },
                                { id: 'investors', label: 'Investors' },
                                { id: 'pipeline', label: 'Pipeline' },
                                { id: 'participants', label: 'Participants' },
                                { id: 'statistics', label: 'Statistics' },
                                { id: 'files', label: 'Files' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setSearchParams({ tab: tab.id });
                                    }}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">Total Applications</p>
                                        <p className="text-3xl font-bold text-gray-900">{stats?.total || participants.length || 0}</p>
                                    </div>
                                    <div className="text-blue-500">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">In Screening</p>
                                        <p className="text-3xl font-bold text-yellow-600">{stats?.screening || 0}</p>
                                    </div>
                                    <div className="text-yellow-500">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">Finalists</p>
                                        <p className="text-3xl font-bold text-orange-600">{stats?.final || 0}</p>
                                    </div>
                                    <div className="text-orange-500">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-500">Selected</p>
                                        <p className="text-3xl font-bold text-green-600">{stats?.selected || 0}</p>
                                    </div>
                                    <div className="text-green-500">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Event Details */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Event Details</h2>
                            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{event.eventDescription || 'No description'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Event Type</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{event.eventType}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Event Date</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{new Date(event.eventDate).toLocaleString()}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Location</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{event.location}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Max Participants</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{event.maxParticipants || 'Unlimited'}</dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                                    <dd className="mt-1">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${event.status === 'UPCOMING' ? 'bg-blue-100 text-blue-800' :
                                            event.status === 'ONGOING' ? 'bg-green-100 text-green-800' :
                                                event.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800' :
                                                    'bg-red-100 text-red-800'
                                            }`}>
                                            {event.status}
                                        </span>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                )}

                {/* Startups Tab */}
                {activeTab === 'startups' && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Startups</h2>
                                <p className="text-sm text-gray-500 mt-1">Manage startups participating in this event</p>
                            </div>
                            <div className="flex space-x-2">
                                {/* Show Create New Startup button for EVENT_ORGANIZER, ADMIN, and TENANT_ADMIN */}
                                {(user?.role === 'EVENT_ORGANIZER' || user?.role === 'ADMIN' || user?.role === 'TENANT_ADMIN') && (
                                    <button
                                        onClick={() => setShowCreateStartupModal(true)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span>Create New Startup</span>
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowAddStartupModal(true)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span>Add Existing Startup</span>
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            {participants.length === 0 ? (
                                <div className="text-center py-12">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No startups yet</h3>
                                    <p className="mt-1 text-sm text-gray-500">Add startups to this event to get started.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {participants.map((participant) => {
                                        const currentStage = participant.stage || participant.currentStage || 'APPLICATION_RECEIVED';
                                        const nextStages = getNextStages(currentStage);

                                        return (
                                            <div key={participant.id} className="px-4 py-4 hover:bg-gray-50 transition">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3 flex-1">
                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center">
                                                            <span className="text-white font-medium">
                                                                {(participant.startup?.organizationName || participant.startupName || 'S')?.charAt(0)?.toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-gray-900">
                                                                {participant.startup?.organizationName || participant.startupName || 'Unknown Startup'}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {participant.startup?.email || ''} • Applied: {participant.applicationDate ? new Date(participant.applicationDate).toLocaleDateString() : 'N/A'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center space-x-3">
                                                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${currentStage === 'SELECTED' ? 'bg-green-100 text-green-800' :
                                                            currentStage === 'FINAL' ? 'bg-orange-100 text-orange-800' :
                                                                currentStage === 'PRELIMINARY' ? 'bg-purple-100 text-purple-800' :
                                                                    currentStage === 'SCREENING' ? 'bg-yellow-100 text-yellow-800' :
                                                                        'bg-blue-100 text-blue-800'
                                                            }`}>
                                                            {getStageLabel(currentStage)}
                                                        </span>
                                                        {nextStages.length > 0 && (
                                                            <div className="relative">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setOpenDropdown(openDropdown === `startup-${participant.id}` ? null : `startup-${participant.id}`);
                                                                    }}
                                                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-1"
                                                                >
                                                                    <span>Move to Next</span>
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                                    </svg>
                                                                </button>
                                                                {openDropdown === `startup-${participant.id}` && (
                                                                    <>
                                                                        <div
                                                                            className="fixed inset-0 z-10"
                                                                            onClick={() => setOpenDropdown(null)}
                                                                        ></div>
                                                                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                                                                            <div className="py-1">
                                                                                {nextStages.map((stage) => (
                                                                                    <button
                                                                                        key={stage}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setOpenDropdown(null);
                                                                                            handleMoveStage(participant.id, stage);
                                                                                        }}
                                                                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                                                                                    >
                                                                                        Move to {getStageLabel(stage)}
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Investors Tab */}
                {activeTab === 'investors' && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Investors</h2>
                                <p className="text-sm text-gray-500 mt-1">Manage investors for this event</p>
                            </div>
                            <button
                                onClick={() => setShowAddInvestorModal(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                <span>Add Investor</span>
                            </button>
                        </div>
                        <div className="p-4">
                            {eventInvestors.length === 0 ? (
                                <div className="text-center py-12">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No investors yet</h3>
                                    <p className="mt-1 text-sm text-gray-500">Add investors to this event.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {eventInvestors.map((investor) => (
                                        <div key={investor.id || investor.participantId} className="px-4 py-3 hover:bg-gray-50 transition">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
                                                        <span className="text-white font-medium">
                                                            {(investor.organizationName || investor.name || 'I')?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {investor.organizationName || investor.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {investor.email} • {investor.organizationType || 'Investor'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                        Active
                                                    </span>
                                                    <button
                                                        onClick={() => handleRemoveInvestor(investor.id, investor.participantId, investor.name || investor.organizationName)}
                                                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
                                                        title="Remove Investor"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pipeline Tab */}
                {activeTab === 'pipeline' && (
                    <div className="space-y-6">
                        {/* Pipeline Overview Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            {stages.map((stage) => {
                                const count = getParticipantsByStage(stage.id).length;
                                const percentage = participants.length > 0 ? ((count / participants.length) * 100).toFixed(0) : 0;

                                // Define actual colors for each stage
                                const colorMap = {
                                    'APPLICATION_RECEIVED': { bg: 'bg-blue-500', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200' },
                                    'SCREENING': { bg: 'bg-yellow-500', text: 'text-yellow-600', light: 'bg-yellow-50', border: 'border-yellow-200' },
                                    'PRELIMINARY': { bg: 'bg-purple-500', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-200' },
                                    'FINAL': { bg: 'bg-orange-500', text: 'text-orange-600', light: 'bg-orange-50', border: 'border-orange-200' },
                                    'SELECTED': { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50', border: 'border-green-200' }
                                };
                                const colors = colorMap[stage.id] || { bg: 'bg-gray-500', text: 'text-gray-600', light: 'bg-gray-50', border: 'border-gray-200' };

                                return (
                                    <div key={stage.id} className={`${colors.light} ${colors.border} border-2 rounded-xl p-4 hover:shadow-lg transition-all duration-200`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-2xl">{stage.icon}</span>
                                            <span className={`text-3xl font-bold ${colors.text}`}>{count}</span>
                                        </div>
                                        <h3 className="font-semibold text-gray-900 text-sm mb-1">{stage.label}</h3>
                                        <div className="flex items-center space-x-2">
                                            <div className="flex-1 bg-white rounded-full h-2 overflow-hidden">
                                                <div
                                                    className={`h-full ${colors.bg} transition-all duration-500`}
                                                    style={{ width: `${percentage}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-xs font-medium text-gray-600">{percentage}%</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Kanban Board Style Pipeline */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-900">Selection Pipeline</h2>
                                <p className="text-sm text-gray-600 mt-1">Drag startups between stages or use the move buttons</p>
                            </div>

                            <div className="overflow-x-auto">
                                <div className="inline-flex min-w-full divide-x divide-gray-200">
                                    {stages.map((stage) => {
                                        const stageParticipants = getParticipantsByStage(stage.id);

                                        // Define actual colors for each stage
                                        const colorMap = {
                                            'APPLICATION_RECEIVED': {
                                                header: 'bg-gradient-to-r from-blue-500 to-blue-600',
                                                accent: 'bg-blue-100',
                                                text: 'text-blue-700',
                                                border: 'border-blue-300'
                                            },
                                            'SCREENING': {
                                                header: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
                                                accent: 'bg-yellow-100',
                                                text: 'text-yellow-700',
                                                border: 'border-yellow-300'
                                            },
                                            'PRELIMINARY': {
                                                header: 'bg-gradient-to-r from-purple-500 to-purple-600',
                                                accent: 'bg-purple-100',
                                                text: 'text-purple-700',
                                                border: 'border-purple-300'
                                            },
                                            'FINAL': {
                                                header: 'bg-gradient-to-r from-orange-500 to-orange-600',
                                                accent: 'bg-orange-100',
                                                text: 'text-orange-700',
                                                border: 'border-orange-300'
                                            },
                                            'SELECTED': {
                                                header: 'bg-gradient-to-r from-green-500 to-green-600',
                                                accent: 'bg-green-100',
                                                text: 'text-green-700',
                                                border: 'border-green-300'
                                            }
                                        };
                                        const colors = colorMap[stage.id] || {
                                            header: 'bg-gradient-to-r from-gray-500 to-gray-600',
                                            accent: 'bg-gray-100',
                                            text: 'text-gray-700',
                                            border: 'border-gray-300'
                                        };

                                        return (
                                            <div key={stage.id} className="flex-shrink-0 w-80 flex flex-col h-[calc(100vh-300px)] min-h-[600px]">
                                                {/* Column Header */}
                                                <div className={`${colors.header} px-4 py-3 text-white sticky top-0 z-10`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-2">
                                                            <span className="text-xl">{stage.icon}</span>
                                                            <div>
                                                                <h3 className="font-bold text-sm">{stage.label}</h3>
                                                                <p className="text-xs opacity-90">{stageParticipants.length} startup{stageParticipants.length !== 1 ? 's' : ''}</p>
                                                            </div>
                                                        </div>
                                                        <div className={`${colors.accent} ${colors.text} px-2 py-1 rounded-full text-xs font-bold`}>
                                                            {stageParticipants.length}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Column Content */}
                                                <div className="flex-1 overflow-y-auto bg-gray-50 p-3 space-y-3">
                                                    {stageParticipants.length === 0 ? (
                                                        <div className="flex flex-col items-center justify-center h-32 text-center">
                                                            <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                            </svg>
                                                            <p className="text-sm text-gray-400 font-medium">No startups</p>
                                                            <p className="text-xs text-gray-300 mt-1">Startups will appear here</p>
                                                        </div>
                                                    ) : (
                                                        stageParticipants.map((participant) => {
                                                            const startupName = participant.startup?.organizationName || participant.startup?.name || participant.startupName || 'Unknown Startup';
                                                            const startupEmail = participant.startup?.email || '';
                                                            const nextStages = getNextStages(stage.id);

                                                            return (
                                                                <div
                                                                    key={participant.id}
                                                                    className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 p-4 group"
                                                                >
                                                                    <div className="flex items-start justify-between mb-3">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center space-x-2 mb-1">
                                                                                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                                                                                    <span className="text-white text-xs font-bold">
                                                                                        {(startupName || 'S').charAt(0).toUpperCase()}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <h4 className="text-sm font-semibold text-gray-900 truncate">{startupName}</h4>
                                                                                    {startupEmail && (
                                                                                        <p className="text-xs text-gray-500 truncate">{startupEmail}</p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Score and Date Info */}
                                                                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                                                        {participant.finalScore || participant.preliminaryScore ? (
                                                                            <div className="flex items-center space-x-1">
                                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                                                </svg>
                                                                                <span className="font-medium">Score: {participant.finalScore || participant.preliminaryScore}</span>
                                                                            </div>
                                                                        ) : (
                                                                            <span>No score yet</span>
                                                                        )}
                                                                        {participant.applicationDate && (
                                                                            <span>{new Date(participant.applicationDate).toLocaleDateString()}</span>
                                                                        )}
                                                                    </div>

                                                                    {/* Move to Next Stage Button */}
                                                                    {nextStages.length > 0 && (
                                                                        <div className="relative">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setOpenDropdown(openDropdown === `pipeline-${participant.id}` ? null : `pipeline-${participant.id}`);
                                                                                }}
                                                                                className="w-full px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition flex items-center justify-center space-x-1 group-hover:bg-blue-50 group-hover:text-blue-700"
                                                                            >
                                                                                <span>Move Forward</span>
                                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                                                </svg>
                                                                            </button>
                                                                            {openDropdown === `pipeline-${participant.id}` && (
                                                                                <>
                                                                                    <div
                                                                                        className="fixed inset-0 z-10"
                                                                                        onClick={() => setOpenDropdown(null)}
                                                                                    ></div>
                                                                                    <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                                                                                        <div className="py-1">
                                                                                            {nextStages.map((nextStage) => (
                                                                                                <button
                                                                                                    key={nextStage}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setOpenDropdown(null);
                                                                                                        handleMoveStage(participant.id, nextStage);
                                                                                                    }}
                                                                                                    className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-100 transition"
                                                                                                >
                                                                                                    Move to {getStageLabel(nextStage)}
                                                                                                </button>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Conversion Metrics */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Conversion Metrics</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {stages.slice(0, -1).map((stage, index) => {
                                    const currentCount = getParticipantsByStage(stage.id).length;
                                    const nextCount = getParticipantsByStage(stages[index + 1].id).length;
                                    const conversionRate = currentCount > 0 ? ((nextCount / currentCount) * 100).toFixed(1) : 0;

                                    return (
                                        <div key={stage.id} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-all">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-medium text-gray-600">{stage.label}</span>
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </div>
                                            <div className="flex items-baseline space-x-2">
                                                <span className="text-2xl font-bold text-gray-900">{conversionRate}%</span>
                                                <span className="text-xs text-gray-500">({nextCount}/{currentCount})</span>
                                            </div>
                                            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                                                <div
                                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                                                    style={{ width: `${Math.min(conversionRate, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Participants Tab */}
                {activeTab === 'participants' && (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Startup</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {participants.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">
                                            No participants found
                                        </td>
                                    </tr>
                                ) : (
                                    participants.map((participant) => {
                                        const currentStage = participant.stage || participant.currentStage || 'APPLICATION_RECEIVED';
                                        const nextStages = getNextStages(currentStage);
                                        const startupName = participant.startup?.organizationName || participant.startup?.name || participant.startupName || 'Unknown Startup';
                                        const startupEmail = participant.startup?.email || '';

                                        return (
                                            <tr key={participant.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3">
                                                    <div className="text-sm font-medium text-gray-900">{startupName}</div>
                                                    {startupEmail && (
                                                        <div className="text-xs text-gray-500">{startupEmail}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${currentStage === 'SELECTED' ? 'bg-green-100 text-green-800' :
                                                        currentStage === 'FINAL' ? 'bg-orange-100 text-orange-800' :
                                                            currentStage === 'PRELIMINARY' ? 'bg-purple-100 text-purple-800' :
                                                                currentStage === 'SCREENING' ? 'bg-yellow-100 text-yellow-800' :
                                                                    'bg-blue-100 text-blue-800'
                                                        }`}>
                                                        {getStageLabel(currentStage)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                    {participant.finalScore || participant.preliminaryScore || participant.score || 'N/A'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                    {participant.applicationDate ? new Date(participant.applicationDate).toLocaleDateString() : (participant.appliedAt ? new Date(participant.appliedAt).toLocaleDateString() : 'N/A')}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                    {nextStages.length > 0 ? (
                                                        <div className="relative inline-block">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOpenDropdown(openDropdown === participant.id ? null : participant.id);
                                                                }}
                                                                className="text-indigo-600 hover:text-indigo-900 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition flex items-center space-x-1"
                                                            >
                                                                <span>Move Stage</span>
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                                </svg>
                                                            </button>
                                                            {openDropdown === participant.id && (
                                                                <>
                                                                    <div
                                                                        className="fixed inset-0 z-10"
                                                                        onClick={() => setOpenDropdown(null)}
                                                                    ></div>
                                                                    <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                                                                        <div className="py-1">
                                                                            {nextStages.map((stage) => (
                                                                                <button
                                                                                    key={stage}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setOpenDropdown(null);
                                                                                        handleMoveStage(participant.id, stage);
                                                                                    }}
                                                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                                                                                >
                                                                                    Move to {getStageLabel(stage)}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">Final Stage</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Statistics Tab */}
                {activeTab === 'statistics' && (
                    <div className="space-y-6">
                        {/* Statistics Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Stage Distribution</h3>
                                <div className="space-y-3">
                                    {stages.map((stage) => {
                                        const count = getParticipantsByStage(stage.id).length;
                                        const percentage = participants.length > 0 ? ((count / participants.length) * 100).toFixed(1) : 0;

                                        return (
                                            <div key={stage.id}>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-gray-700">{stage.label}</span>
                                                    <span className="font-semibold text-gray-900">{count} ({percentage}%)</span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className={`bg-${stage.color}-600 h-2 rounded-full transition-all duration-500`}
                                                        style={{ width: `${percentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h3>
                                <dl className="space-y-4">
                                    <div className="flex justify-between">
                                        <dt className="text-sm text-gray-500">Total Applications</dt>
                                        <dd className="text-sm font-semibold text-gray-900">{participants.length}</dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-sm text-gray-500">Acceptance Rate</dt>
                                        <dd className="text-sm font-semibold text-gray-900">
                                            {participants.length > 0 ? ((getParticipantsByStage('SELECTED').length / participants.length) * 100).toFixed(1) : 0}%
                                        </dd>
                                    </div>
                                    <div className="flex justify-between">
                                        <dt className="text-sm text-gray-500">Average Score</dt>
                                        <dd className="text-sm font-semibold text-gray-900">
                                            {participants.filter(p => p.score).length > 0
                                                ? (participants.reduce((sum, p) => sum + (p.score || 0), 0) / participants.filter(p => p.score).length).toFixed(1)
                                                : 'N/A'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </div>
                )}

                {/* Files Tab */}
                {activeTab === 'files' && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Event Files</h3>
                            <p className="text-sm text-gray-500 mt-1">Upload and manage files related to this event</p>
                        </div>
                        <div className="p-6">
                            {/* File Upload */}
                            <div className="mb-6">
                                <FileUpload
                                    moduleType="PITCH_EVENT"
                                    moduleId={id}
                                    defaultCategory="GENERAL"
                                    defaultVisibility="PRIVATE"
                                    onUploadSuccess={(fileName) => {
                                        setAlert({
                                            isOpen: true,
                                            title: 'Success',
                                            message: `File "${fileName}" uploaded successfully!`,
                                            type: 'success'
                                        });
                                        loadFiles();
                                    }}
                                    onUploadError={(error) => {
                                        setAlert({
                                            isOpen: true,
                                            title: 'Error',
                                            message: error || 'Failed to upload file',
                                            type: 'error'
                                        });
                                    }}
                                />
                            </div>

                            {/* File List */}
                            {loadingFiles ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : files.length === 0 ? (
                                <div className="text-center py-12">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No files uploaded yet</h3>
                                    <p className="mt-1 text-sm text-gray-500">Upload event documents, presentations, or other related files above.</p>
                                </div>
                            ) : (
                                <FileList
                                    files={files}
                                    onFileUpdated={loadFiles}
                                    onFilePreview={(fileId) => {
                                        const file = files.find(f => f.id === fileId);
                                        if (file) {
                                            setPreviewFile(file);
                                            setShowPreviewModal(true);
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            {showEditModal && (
                <CreatePitchEventModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        loadEventDetails();
                    }}
                    initialData={event}
                    isEdit={true}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Event?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete "{event?.eventName}"? This action cannot be undone and will remove all participants and data associated with this event.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Startup Modal */}
            {showAddStartupModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Add Startup to Event</h3>
                            <button
                                onClick={() => setShowAddStartupModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto flex-1">
                            {availableStartups.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">All available startups are already added to this event.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {availableStartups.map((startup) => (
                                        <div
                                            key={startup.id}
                                            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                                            onClick={() => handleAddStartup(startup.id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center">
                                                        <span className="text-white font-medium">
                                                            {(startup.organizationName || startup.name || 'S')?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {startup.organizationName || startup.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">{startup.email}</p>
                                                    </div>
                                                </div>
                                                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add Investor Modal */}
            {showAddInvestorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Add Investor to Event</h3>
                            <button
                                onClick={() => setShowAddInvestorModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto flex-1">
                            {availableInvestors.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500">No investors available.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {availableInvestors.map((investor) => (
                                        <div
                                            key={investor.id}
                                            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition"
                                            onClick={() => handleAddInvestor(investor.id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
                                                        <span className="text-white font-medium">
                                                            {(investor.organizationName || investor.name || 'I')?.charAt(0)?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {investor.organizationName || investor.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">{investor.email}</p>
                                                    </div>
                                                </div>
                                                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => { })}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* File Preview Modal */}
            <FilePreviewModal
                file={previewFile}
                isOpen={showPreviewModal}
                onClose={() => {
                    setShowPreviewModal(false);
                    setPreviewFile(null);
                }}
            />

            {/* Create Startup Modal */}
            <CreateStartupModal
                isOpen={showCreateStartupModal}
                onClose={() => setShowCreateStartupModal(false)}
                onSuccess={handleStartupCreated}
                isEdit={false}
            />
        </>
    );
};

export default PitchEventDetail;
